// @flow
import React from 'react';
import classNames from 'classnames';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import GeoDistance from '../GeoDistance/GeoDistance';
import RankedBadgesCollection from '../RankedBadgesCollection/RankedBadgesCollection';
import UrgencyMessage from '../UrgencyMessage/UrgencyMessage';
import BadgeTooltip from './ReviewBadgeTooltip';
import PriceSummary from '@homeaway/react-price-summary';
import {
    VRBO_OPAS_DUALPRICE_EU,
    VRBO_OPAS_DUALPRICE_TAXEXLUDED,
    VRBO_OPAS_DUALPRICE_US
} from '../../abacus/experiments';
import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';
import Experiment, {On, Off} from '@homeaway/react-experiment';
import {DualPrice} from '../DualPrice/DualPrice';
import {PP_ACCEPTED} from '../../constants/badges';

type Props = {
    rankedBadges: Badge[],
    superlativesBadges: Badge[],
    freeCancellationBadge: Badge
} & HitInfoProps;

const Superlative = ({superlativesBadges, isDualPrice}) => {
    const SuperlativeClassNames = classNames('HitInfo__superlative', {
        'HitInfo__superlative--dualPrice': isDualPrice
    });
    if (!superlativesBadges[0].helpText) {
        return (
            <span className={SuperlativeClassNames}>
                {superlativesBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span className={SuperlativeClassNames}>
                {superlativesBadges[0].name}
            </span>
            <BadgeTooltip helpText={superlativesBadges[0].helpText} />
        </span>
    );
};

// TODO: move minStay logic to lingua
// https://jira.homeawaycorp.com/browse/SERP-2848
export default function HitInfoMobile({
    brand,
    rankedBadges,
    intl,
    isDatelessSearch,
    isMetric,
    listing,
    fullLocale,
    listingHasSecondaryPrice,
    superlativesBadges,
    freeCancellationBadge
}: Props) {
    const {instantBookable, reviewCount, geoDistance, unitMessage} = listing;

    // prevents undesired content shift if there is no reviewBadge
    let priceDetailsClass =
        superlativesBadges &&
        superlativesBadges[0] &&
        superlativesBadges[0].helpText
            ? 'HitInfo__priceSuperlativeGroup--extended'
            : 'HitInfo__priceSuperlativeGroup';

    const SinglePrice = () => {
        return (
            <div className={priceDetailsClass}>
                <div
                    className={classNames('HitInfo__price', {
                        'HitInfo__Price--condensed': listingHasSecondaryPrice
                    })}
                >
                    {listingHasSecondaryPrice &&
                        !!superlativesBadges &&
                        !!superlativesBadges.length && (
                            <Superlative
                                superlativesBadges={superlativesBadges}
                                isDualPrice={false}
                            />
                        )}
                    <Price
                        listing={filterGraphQlAnywhere(
                            priceFragments.listing,
                            listing
                        )}
                        useAverageLabel={isDatelessSearch}
                        fullLocale={fullLocale}
                        instantBookable={instantBookable}
                        intl={intl}
                    />
                    {listingHasSecondaryPrice && listing.priceSummarySecondary && (
                        <span className="PriceSummary__secondary">
                            <PriceSummary
                                priceSummary={listing.priceSummarySecondary}
                            />
                        </span>
                    )}
                </div>
                {!listingHasSecondaryPrice &&
                    !!superlativesBadges &&
                    !!superlativesBadges.length && (
                        <Superlative
                            superlativesBadges={superlativesBadges}
                            isDualPrice={false}
                        />
                    )}
            </div>
        );
    };

    const BadgeRatingGroup = ({isDualPrice}) => {
        const premierPartnerBadge = rankedBadges.find(
            (badge) => badge.id === PP_ACCEPTED
        );
        return (
            <div className="HitInfo__badgeRatingGroup">
                {reviewCount > 0 && (
                    <div className="HitInfo__rating">
                        <Rating
                            listing={filterGraphQlAnywhere(
                                ratingFragments.listing,
                                listing
                            )}
                            isDualPrice={isDualPrice}
                        />
                    </div>
                )}
                {!isDualPrice && premierPartnerBadge && (
                    <div
                        className="small"
                        data-wdio="premier-partner-badge"
                        aria-hidden="true"
                    >
                        {premierPartnerBadge.name}
                    </div>
                )}
            </div>
        );
    };

    const DualPriceOn = () => {
        return (
            <On>
                <div className="media-flex__row">
                    <BadgeRatingGroup isDualPrice />
                    {!!superlativesBadges && !!superlativesBadges.length && (
                        <Superlative
                            superlativesBadges={superlativesBadges}
                            isDualPrice
                        />
                    )}
                </div>
                <div className="HitInfo__details">
                    <Details
                        listing={listing}
                        isMetric={isMetric}
                        locale={intl.locale}
                    />
                </div>
                {geoDistance && (
                    <GeoDistance geoDistance={geoDistance} brand={brand} />
                )}
                <RankedBadgesCollection
                    rankedBadges={rankedBadges}
                    isDualPrice
                />
                <div className={priceDetailsClass}>
                    <DualPrice
                        listing={listing}
                        intl={intl}
                        isDesktop={false}
                    />
                </div>
            </On>
        );
    };

    return (
        <Experiment name={VRBO_OPAS_DUALPRICE_US}>
            <div className="media-flex__left">
                <UrgencyMessage
                    message={unitMessage}
                    freeCancellationBadge={freeCancellationBadge}
                />
                <DualPriceOn />
                <Off>
                    <Experiment name={VRBO_OPAS_DUALPRICE_EU}>
                        <DualPriceOn />
                        <Off>
                            <Experiment name={VRBO_OPAS_DUALPRICE_TAXEXLUDED}>
                                <DualPriceOn />
                                <Off>
                                    <SinglePrice />
                                    <div className="HitInfo__details">
                                        <Details
                                            listing={listing}
                                            isMetric={isMetric}
                                            locale={intl.locale}
                                        />
                                    </div>
                                    {geoDistance && (
                                        <GeoDistance
                                            geoDistance={geoDistance}
                                            brand={brand}
                                        />
                                    )}
                                    <BadgeRatingGroup isDualPrice={false} />
                                    <RankedBadgesCollection
                                        isDualPrice={false}
                                        rankedBadges={rankedBadges}
                                    />
                                </Off>
                            </Experiment>
                        </Off>
                    </Experiment>
                </Off>
            </div>
        </Experiment>
    );
}
